/* eslint-disable no-param-reassign */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "@shared/v2/Modal";
import Button from "@shared/v2/Button/Button";
import MlsListingDropdown from "@shared/MlsListingDropdown";
import { ArrowLeft } from "@shared/v2/Icomoon";
import TextButton from "@shared/v2/TextButton";
import useMlsListingDetails from "@shared/MlsListingDropdown/useMlsListingDetails";
import { mergeCompareObjects, setDefaultValues } from "../api/helpers";
import { allKeys } from "../api/constants";
import ListingPreviewCard from "../../../../Transactions/ListingPreviewCard";
import ReviewImportListingDetails from "./ReviewImportListingDetails";

const getPropertyId = (accountPropertyTypes, labelToFind) => {
  const property = accountPropertyTypes.find((p) => p.label === labelToFind);
  return property ? property.value : null;
};

const PROPERTY_MAPPINGS = [
  { listingProp: "property_type", payloadProp: "property_type" },
  { listingProp: "price", payloadProp: "price" },
  { listingProp: "mlsNumber", payloadProp: "mls_number" },
  { listingProp: "description", payloadProp: "description" },
  { listingProp: "photosAvailable", payloadProp: "photosAvailable" },
  { listingProp: "streetAddress", payloadProp: "street_address", isAddressAttr: true },
  { listingProp: "city", payloadProp: "city", isAddressAttr: true },
  { listingProp: "state", payloadProp: "locality", isAddressAttr: true },
  { listingProp: "postalCode", payloadProp: "postal_code", isAddressAttr: true },
  { listingProp: "county", payloadProp: "county", isAddressAttr: true },
  { listingProp: "neighborhood", payloadProp: "neighborhood", isAddressAttr: true },
  { listingProp: "beds", payloadProp: "beds", isDetailAttr: true },
  { listingProp: "baths", payloadProp: "baths", isDetailAttr: true },
  { listingProp: "sqFt", payloadProp: "sqft", isDetailAttr: true },
  { listingProp: "year_built", payloadProp: "year_built", isDetailAttr: true },
  { listingProp: "parking", payloadProp: "parking", isDetailAttr: true },
];

const addToPayload = (payload, payloadProperty, isAddressAttr, isDetailAttr, propertyValue) => {
  if (isAddressAttr) {
    if (!payload.address_attributes) {
      payload.address_attributes = { address_type: "listing" };
    }
    payload.address_attributes[payloadProperty] = propertyValue;
  } else if (isDetailAttr) {
    if (!payload.details) {
      payload.details = [];
    }
    payload.details.push({ name: payloadProperty, value: propertyValue });
  } else {
    payload[payloadProperty] = propertyValue;
  }
};

const setProperty = (payload, data, listingProperty, payloadProperty, isAddressAttr, isDetailAttr) => {
  const propData = data[listingProperty];
  if (!propData) return;

  const mlsProperty = propData[`mls_${listingProperty}`];
  const currentProperty =
    propData[`current_${listingProperty}`] === "—" ? null : propData[`current_${listingProperty}`];

  const { isSelectable, mlsChecked } = propData;

  if (isSelectable) {
    if (mlsChecked) {
      addToPayload(payload, payloadProperty, isAddressAttr, isDetailAttr, mlsProperty);
    } else {
      addToPayload(payload, payloadProperty, isAddressAttr, isDetailAttr, currentProperty);
    }
  } else if (mlsProperty !== "—") {
    addToPayload(payload, payloadProperty, isAddressAttr, isDetailAttr, mlsProperty);
  } else {
    addToPayload(payload, payloadProperty, isAddressAttr, isDetailAttr, currentProperty);
  }
};

const ImportListingDetailsModal = ({
  modalOpen,
  isFromDetail,
  closeModal,
  uuid,
  isFromIndex,
  listingUuid,
  onGetAllDetailsAsThunk,
  onGetPropertyTypesAsThunk,
  importDetailAsThunk,
  photosAvailable,
  propertyDetails,
  accountPropertyTypes,
  listingDetailsData,
  checkAddressAndcloseModal,
  status,
  preStatus,
  closeModalAsThunk,
  setToggleModal,
  openUpdateTransactionModal,
  closeImportModal,
  revertStatus,
  title,
}) => {
  const [selectedBlossorId, setSelectedBlossorId] = useState(null);
  const [openReview, setOpenReview] = useState(false);
  const [listingData, setListingData] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const resetState = () => {
    setSelectedBlossorId(null);
    setOpenReview(false);
    setListingData(null);
    setIsImporting(false);
  };

  const mlsListingDetails = useMlsListingDetails(selectedBlossorId);
  const updatedMlsListingDetails = mlsListingDetails.data
    ? {
        streetAddress: mlsListingDetails.data?.streetAddress,
        postalCode: mlsListingDetails.data?.postalCode,
        price: mlsListingDetails.data?.price,
        mlsNumber: mlsListingDetails.data?.mlsNum,
        beds: mlsListingDetails.data?.bedrooms,
        baths: mlsListingDetails.data?.bathsTotalDecimal,
        sqFt: mlsListingDetails.data?.squareFeet,
        city: mlsListingDetails.data?.city,
        state: mlsListingDetails.data?.state,
        county: mlsListingDetails.data?.county,
        property_type: mlsListingDetails.data?.standardPropertyType || mlsListingDetails.data?.propertyType,
        neighborhood: mlsListingDetails.data?.neighborhood,
        year_built: mlsListingDetails.data?.year,
        parking: String(mlsListingDetails.data?.parkingFeatures || []),
        photosAvailable: mlsListingDetails.data?.photosAvailable,
        photos: mlsListingDetails.data?.photos,
        description: mlsListingDetails.data?.remarks,
        blossorId: mlsListingDetails.data?.blossorId,
      }
    : null;

  useEffect(() => {
    if (isFromIndex && listingUuid) {
      onGetAllDetailsAsThunk(listingUuid);
      onGetPropertyTypesAsThunk(listingUuid);
    }
  }, [isFromIndex]);

  const handleReview = () => {
    const currentListingData = {
      ...propertyDetails,
      ...listingDetailsData,
      streetAddress: propertyDetails.street_address,
      sqFt: propertyDetails.sqft,
      postalCode: propertyDetails.zip,
      property_type: propertyDetails?.property_type?.name,
      photosAvailable,
      mlsNumber: listingDetailsData.mls_number,
    };

    const comparableListingData = mergeCompareObjects(
      setDefaultValues(updatedMlsListingDetails, allKeys),
      setDefaultValues(currentListingData, allKeys),
    );
    setListingData(comparableListingData);
    setOpenReview(true);
  };

  const handleImport = () => {
    setIsImporting(true);
    const payloadListing = {};

    PROPERTY_MAPPINGS.forEach(({ listingProp, payloadProp, isAddressAttr, isDetailAttr }) => {
      setProperty(payloadListing, listingData, listingProp, payloadProp, isAddressAttr, isDetailAttr);
    });

    if (payloadListing.property_type) {
      const propertyTypeId = getPropertyId(accountPropertyTypes, payloadListing.property_type);
      if (propertyTypeId) {
        payloadListing.property_type_id = propertyTypeId;
      }
      delete payloadListing.property_type;
    }
    if (payloadListing.photosAvailable) {
      payloadListing.blossor_id = mlsListingDetails.data.blossorId;
      payloadListing.use_mls_photos = true;
      delete payloadListing.photosAvailable;
    }

    const userUUID = isFromIndex && listingUuid ? listingUuid : uuid;

    if (importDetailAsThunk) {
      importDetailAsThunk(userUUID, payloadListing, () => setIsImporting(false));
    } else {
      closeImportModal();
      openUpdateTransactionModal(true, {
        ...mlsListingDetails.data,
        importPayload: payloadListing,
      });
      closeModal();
      resetState();
    }
  };

  const handleBack = () => {
    setOpenReview(false);
  };

  const handleClose = () => {
    closeModal();
    resetState();

    const userUUID = isFromIndex && listingUuid ? listingUuid : uuid;
    if (preStatus && userUUID) {
      revertStatus(userUUID, preStatus);
    }

    if (isFromDetail) return;

    if (checkAddressAndcloseModal) {
      checkAddressAndcloseModal();
    } else {
      closeModalAsThunk(preStatus);
      if (!propertyDetails?.street_address) {
        setToggleModal(true, status, preStatus);
      }
    }
  };

  const clearListing = () => {
    setOpenReview(false);
    mlsListingDetails.actions.clear();
  };

  return (
    <Modal
      className="tw-flex tw-justify-center tw-items-center"
      dialogClassName="tw-w-[636px] tw-h-auto"
      contentClassName="tw-w-full tw-h-full"
      backdrop="static"
      show={modalOpen}
      onHide={handleClose}
      data-cy="import-listing-detail-modal"
    >
      <Modal.Header titleClassName="tw-mb-12px tw-text-neutral-gray-75" title={title} closeButton />
      <div className="tw-mb-32px tw-text-gray-50 tw-text-14d">
        Would you like to automatically import listing details from the MLS?
      </div>
      <Modal.Body className="tw-mb-32px">
        {!openReview && !mlsListingDetails.data && (
          <div className="tw-border-1px tw-border-gray-10 tw-rounded-[8px] tw-p-[16px] tw-border-solid">
            <div className="tw-text-gray-base tw-text-14d tw-font-semibold tw-mb-[11px]">
              Import Listing Details from MLS
            </div>
            <MlsListingDropdown
              onChange={(option) => setSelectedBlossorId(option?.value)}
              placeholder="Search by Listing Address or MLS #"
              defaultSearchTerm={listingDetailsData?.mls_number || propertyDetails?.street_address}
            />
            <div className="tw-text-gray-50 tw-text-12d tw-mt-[4px]">(Optional, but recommended)</div>
          </div>
        )}

        {!openReview && mlsListingDetails.data && (
          <ListingPreviewCard
            containerClassName="tw-w-full"
            listingData={mlsListingDetails.data}
            clearListing={clearListing}
          />
        )}

        {openReview && (
          <ReviewImportListingDetails
            containerClassName="tw-w-[632px] tw-h-[510px]"
            setListingData={setListingData}
            listingData={listingData}
          />
        )}
      </Modal.Body>

      <Modal.Footer className="tw-flex">
        <Button
          onClick={openReview ? handleBack : handleClose}
          className="tw-px-[20px] tw-py-[8px] tw-flex tw-items-center tw-mr-auto"
          schema="tertiary"
          size="medium"
          data-cy="documents-upload-cancel-button"
        >
          {openReview ? (
            <>
              <ArrowLeft />
              <span className="tw-pl-[7px]">Back</span>
            </>
          ) : (
            "Cancel"
          )}
        </Button>
        {!openReview && (
          <TextButton
            className="tw-pr-[16px] tw-text-blue-50 tw-font-bold"
            size="medium"
            onClick={() => {
              closeImportModal();
              openUpdateTransactionModal(true, null);
              closeModal();
              resetState();
            }}
          >
            SKIP
          </TextButton>
        )}
        <Button
          className="tw-flex tw-items-center"
          size="medium"
          schema="primary"
          disabled={!mlsListingDetails.data}
          isLoading={isImporting}
          onClick={openReview ? handleImport : handleReview}
          data-cy="import-listing-detail-review-button"
        >
          {openReview ? "Import" : "Review"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ImportListingDetailsModal.propTypes = {
  modalOpen: PropTypes.bool,
  isFromDetail: PropTypes.bool,
  isFromIndex: PropTypes.bool,
  listingUuid: PropTypes.string,
  closeModal: PropTypes.func,
  checkAddressAndcloseModal: PropTypes.func,
  uuid: PropTypes.string,
  onGetAllDetailsAsThunk: PropTypes.func,
  onGetPropertyTypesAsThunk: PropTypes.func,
  importDetailAsThunk: PropTypes.func,
  photosAvailable: PropTypes.number,
  status: PropTypes.string,
  preStatus: PropTypes.string,
  closeModalAsThunk: PropTypes.func,
  setToggleModal: PropTypes.func,
  openUpdateTransactionModal: PropTypes.func,
  closeImportModal: PropTypes.func,
  revertStatus: PropTypes.func,
  title: PropTypes.string,
  listingDetailsData: PropTypes.shape({
    mls_number: PropTypes.string,
    price: PropTypes.string,
    description: PropTypes.string,
  }),
  accountPropertyTypes: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.number }),
  ),
  propertyDetails: PropTypes.shape({
    street_address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    neighborhood: PropTypes.string,
    county: PropTypes.string,
    baths: PropTypes.string,
    beds: PropTypes.string,
    sqft: PropTypes.string,
    year_built: PropTypes.string,
    property_type: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  }),
};

ImportListingDetailsModal.defaultProps = {
  modalOpen: false,
  isFromDetail: false,
  isFromIndex: false,
  listingUuid: null,
  title: "Change Status to Pending",
  closeModal: () => {},
  checkAddressAndcloseModal: null,
  photosAvailable: 0,
  status: null,
  preStatus: null,
  closeModalAsThunk: () => {},
  setToggleModal: () => {},
  openUpdateTransactionModal: () => {},
  closeImportModal: () => {},
  revertStatus: () => {},
  onGetAllDetailsAsThunk: () => {},
  onGetPropertyTypesAsThunk: () => {},
  importDetailAsThunk: null,
  listingDetailsData: {
    mls_number: "",
    price: "",
    description: "",
  },
  uuid: null,
  accountPropertyTypes: [],
  propertyDetails: {
    street_address: null,
    city: null,
    state: null,
    zip: null,
    neighborhood: null,
    county: null,
    baths: null,
    beds: null,
    sqft: null,
    year_built: null,
    property_type: { id: null, name: null },
  },
};

export default ImportListingDetailsModal;
